import React, { useContext, useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AppSettings } from './../../config/app-settings.js';
import authService from '../../services/authService';
import useAuth from '../../hooks/useAuth';
import QRCode from 'react-qr-code';

function PagesLogin() {
	const context = useContext(AppSettings);
	const { isAuthenticated, revalidate } = useAuth();
	const [formData, setFormData] = useState({
		username: '',
		password: '',
		rememberMe: false
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [stage, setStage] = useState('login'); // 'login' | 'mfa' | 'mfa_setup'
	const [otpCode, setOtpCode] = useState('');
	const [otpauthUrl, setOtpauthUrl] = useState('');
	const [mfaSecret, setMfaSecret] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [capsLockOn, setCapsLockOn] = useState(false);
	const usernameRef = useRef(null);
	const passwordRef = useRef(null);
	
	useEffect(() => {
		// Ensure the login view removes header/sidebar and margins
		context.setAppHeaderNone(true);
		context.setAppSidebarNone(true);
		context.setAppContentClass('p-0');
		
		return function cleanUp() {
			context.setAppHeaderNone(false);
			context.setAppSidebarNone(false);
			context.setAppContentClass('');
		};
		
		// eslint-disable-next-line
	}, []);

	const validateField = (name, value) => {
		const errors = { ...fieldErrors };
		
		switch (name) {
			case 'username':
				if (!value.trim()) {
					errors.username = 'Username is required';
				} else if (value.length < 3) {
					errors.username = 'Username must be at least 3 characters';
				} else {
					delete errors.username;
				}
				break;
			case 'password':
				if (!value) {
					errors.password = 'Password is required';
				} else if (value.length < 6) {
					errors.password = 'Password must be at least 6 characters';
				} else {
					delete errors.password;
				}
				break;
			default:
				break;
		}
		
		setFieldErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		const newValue = type === 'checkbox' ? checked : value;
		
		setFormData(prev => ({
			...prev,
			[name]: newValue
		}));
		
		// Clear global error when user types
		if (error) setError('');
		
		// Validate field on blur or when it has content
		if (name !== 'rememberMe' && value.trim()) {
			validateField(name, newValue);
		}
	};

	const handleFieldBlur = (e) => {
		const { name, value } = e.target;
		validateField(name, value);
	};
	
	const handleSubmit = async (event) => {
		event.preventDefault();
		
		// Validate all fields before submit
		const isUsernameValid = validateField('username', formData.username);
		const isPasswordValid = validateField('password', formData.password);
		
		if (!isUsernameValid || !isPasswordValid) {
			setError('Please correct the errors below');
			return;
		}
		
		setLoading(true);
		setError('');
		setSuccess('');
		
		try {
			const result = await authService.login(formData.username, formData.password);
			
			if (result.success && result.mfa_required) {
				setStage('mfa');
				setSuccess('Please enter the 6-digit code from your authenticator app.');
				return;
			}
			if (result.success && result.mfa_setup_required) {
				setStage('mfa_setup');
				setOtpauthUrl(result.otpauth_url || '');
				setMfaSecret(result.secret || '');
				setSuccess('Scan the QR code with your authenticator app, then enter the 6-digit code to enable MFA.');
				return;
			}
			if (result.success) {
				setSuccess('Login successful! Redirecting...');
				setTimeout(async () => {
					await revalidate();
				}, 500);
			} else {
				setError(result.message || 'Login failed. Please check your credentials and try again.');
			}
		} catch (error) {
			console.error('Login error:', error);
			setError('Network error. Please check your connection and try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyMfa = async (e) => {
		e && e.preventDefault();
		
		if (otpCode.length !== 6) {
			setError('Please enter a 6-digit code');
			return;
		}
		
		setLoading(true);
		setError('');
		try {
			const result = await authService.verifyMfaCode(otpCode);
			if (result.success) {
				setSuccess('Authentication successful! Redirecting...');
				setTimeout(async () => {
					await revalidate();
				}, 500);
			} else {
				setError(result.message || 'Invalid code. Please try again.');
				setOtpCode(''); // Clear invalid code
			}
		} catch (err) {
			console.error('MFA verify error:', err);
			setError('Network error. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handleVerifySetup = async (e) => {
		e && e.preventDefault();
		
		if (otpCode.length !== 6) {
			setError('Please enter a 6-digit code');
			return;
		}
		
		setLoading(true);
		setError('');
		try {
			const result = await authService.verifyMfaSetup(otpCode);
			if (result.success) {
				setSuccess('MFA enabled successfully! Redirecting...');
				setTimeout(async () => {
					await revalidate();
				}, 500);
			} else {
				setError(result.message || 'Invalid code. Please try again.');
				setOtpCode(''); // Clear invalid code
			}
		} catch (err) {
			console.error('MFA setup verify error:', err);
			setError('Network error. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const resetToLogin = () => {
		setStage('login');
		setOtpCode('');
		setOtpauthUrl('');
		setMfaSecret('');
		setSuccess('');
		setError('');
		setFieldErrors({});
	};

	const togglePasswordVisibility = () => {
		setShowPassword(!showPassword);
	};

	const handlePasswordKeyEvents = (e) => {
		const isCaps = e.getModifierState && e.getModifierState('CapsLock');
		setCapsLockOn(!!isCaps);
	};

	const isFormValid = formData.username.trim().length >= 3 && 
	                   formData.password.length >= 6 && 
	                   Object.keys(fieldErrors).length === 0;

	if (isAuthenticated) {
		// Redirect to a page available to regular users so the chrome becomes visible immediately
		return <Navigate to='/small-pentest' replace />;
	}
	
	return (
		<div className="login d-flex align-items-center justify-content-center" style={{minHeight: '100vh'}}>
			<div className="login-content" style={{width: '100%', maxWidth: '500px', padding: '2rem', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '10px', backdropFilter: 'blur(10px)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'}}>
				{stage === 'login' && (
					<form onSubmit={handleSubmit} style={{width: '100%'}} noValidate>
						<h1 className="text-center" style={{color: '#fff', marginBottom: '0.5rem', position: 'relative', zIndex: 10}}>Cyber Point Sign In</h1>
						<div className="text-center mb-4" style={{color: 'rgba(255, 255, 255, 0.8)', position: 'relative', zIndex: 10, fontSize: '0.9rem'}}>
							For your protection, please verify your identity.
						</div>
						
						{/* Error Message */}
						{error && (
							<div className="alert alert-danger mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-exclamation-circle me-2"></i>
								{error}
							</div>
						)}
						
						{/* Success Message */}
						{success && (
							<div className="alert alert-success mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-check-circle me-2"></i>
								{success}
							</div>
						)}
						
						<div className="mb-3" style={{position: 'relative', zIndex: 10}}>
							<label className="form-label" style={{color: '#fff', fontWeight: '500'}}>
								Username <span className="text-danger">*</span>
							</label>
							<div className="position-relative">
								<input 
									type="text" 
									name="username"
									value={formData.username}
									onChange={handleInputChange}
									onBlur={handleFieldBlur}
									onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); passwordRef.current && passwordRef.current.focus(); } }}
									ref={usernameRef}
									className={`form-control form-control-lg ${fieldErrors.username ? 'is-invalid' : formData.username.length >= 3 ? 'is-valid' : ''}`}
									style={{backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 255, 255, 0.3)', color: '#333', paddingRight: '3rem'}}
									placeholder="Enter your username"
									required
									disabled={loading}
									autoComplete="username"
									autoFocus
								/>
								{formData.username.length >= 3 && !fieldErrors.username && (
									<i className="fas fa-check position-absolute" style={{right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#28a745'}}></i>
								)}
							</div>
							{fieldErrors.username && (
								<div className="invalid-feedback d-block">
									{fieldErrors.username}
								</div>
							)}
						</div>
						
						<div className="mb-3" style={{position: 'relative', zIndex: 10}}>
							<div className="d-flex">
								<label className="form-label" style={{color: '#fff', fontWeight: '500'}}>
									Password <span className="text-danger">*</span>
								</label>
								<a href="mailto:forgot@cyber-point.nl" className="ms-auto text-decoration-none" style={{color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem'}}>
									Forgot password?
								</a>
							</div>
							<div className="position-relative">
								<input 
									type={showPassword ? "text" : "password"}
									name="password"
									value={formData.password}
									onChange={handleInputChange}
									onBlur={(e) => { handleFieldBlur(e); setCapsLockOn(false); }}
									onKeyDown={handlePasswordKeyEvents}
									onKeyUp={handlePasswordKeyEvents}
									ref={passwordRef}
									className={`form-control form-control-lg ${fieldErrors.password ? 'is-invalid' : formData.password.length >= 6 ? 'is-valid' : ''}`}
									style={{backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 255, 255, 0.3)', color: '#333', paddingRight: '3rem'}}
									placeholder="Enter your password"
									required
									disabled={loading}
									autoComplete="current-password"
								/>
								<button
									type="button"
									className="btn position-absolute"
									style={{right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#6c757d', padding: '0.25rem'}}
									onClick={togglePasswordVisibility}
									disabled={loading}
									tabIndex={-1}
								>
									<i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
								</button>
							</div>
							{fieldErrors.password && (
								<div className="invalid-feedback d-block">
									{fieldErrors.password}
								</div>
							)}
						</div>
						
						<div className="mb-3" style={{position: 'relative', zIndex: 10}}>
							<div className="form-check">
								<input 
									className="form-check-input" 
									type="checkbox" 
									id="rememberMe"
									name="rememberMe"
									checked={formData.rememberMe}
									onChange={handleInputChange}
									disabled={loading}
									style={{backgroundColor: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(255, 255, 255, 0.5)'}}
								/>
								<label className="form-check-label" htmlFor="rememberMe" style={{color: '#fff', marginLeft: '0.5rem', fontSize: '0.9rem'}}>
									Remember me for 30 days
									<i className="fas fa-info-circle ms-1" title="Keeps you signed in on this device for 30 days. Avoid enabling on shared or public computers."></i>
								</label>
							</div>
						</div>
						
						<button 
							type="submit" 
							className="btn btn-outline-theme d-block w-100 fw-500 mb-3"
							style={{
								backgroundColor: isFormValid ? 'rgba(0, 123, 255, 0.9)' : 'rgba(0, 123, 255, 0.5)', 
								border: '1px solid rgba(0, 123, 255, 0.9)', 
								color: '#fff', 
								position: 'relative', 
								zIndex: 10,
								transition: 'all 0.2s ease',
								padding: '0.75rem'
							}}
							disabled={loading || !isFormValid}
						>
							{loading ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Signing In...
								</>
							) : (
								<>
									<i className="fas fa-sign-in-alt me-2"></i>
									Sign In
								</>
							)}
						</button>
						
						<div className="text-center" style={{color: 'rgba(255, 255, 255, 0.8)', position: 'relative', zIndex: 10}}>
							<small>
								Don't have an account yet? 
								<Link to="mailto:register@cyber-point.nl" style={{color: 'rgba(0, 123, 255, 0.9)', textDecoration: 'none', marginLeft: '4px'}}>
									Ask admin to create
								</Link>.
							</small>
						</div>
					</form>
				)}

				{stage === 'mfa' && (
					<form onSubmit={handleVerifyMfa} style={{width: '100%'}} noValidate>
						<h1 className="text-center" style={{color: '#fff', marginBottom: '0.5rem'}}>
							<i className="fas fa-shield-alt me-2"></i>
							Multi‑Factor Authentication
						</h1>
						<div className="text-center mb-4" style={{color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem'}}>
							Enter the code from your authenticator app
						</div>
						
						{error && (
							<div className="alert alert-danger mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-exclamation-circle me-2"></i>
								{error}
							</div>
						)}
						{success && (
							<div className="alert alert-success mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-check-circle me-2"></i>
								{success}
							</div>
						)}
						
						<div className="mb-4">
							<label className="form-label" style={{color: '#fff', fontWeight: '500'}}>
								Authentication Code
							</label>
							<input
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={6}
								value={otpCode}
								onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
								className={`form-control form-control-lg text-center ${otpCode.length === 6 ? 'is-valid' : ''}`}
								style={{
									backgroundColor: 'rgba(255, 255, 255, 0.9)', 
									border: '1px solid rgba(255, 255, 255, 0.3)', 
									color: '#333',
									fontSize: '1.5rem',
									letterSpacing: '0.5rem',
									fontFamily: 'monospace'
								}}
								placeholder="000000"
								required
								autoFocus
								disabled={loading}
								autoComplete="one-time-code"
							/>
							<div className="form-text text-center mt-2" style={{color: 'rgba(255, 255, 255, 0.7)'}}>
								Enter the 6-digit code from your authenticator app
							</div>
						</div>
						
						<button 
							type="submit"
							className="btn btn-outline-theme d-block w-100 fw-500 mb-2"
							style={{
								backgroundColor: otpCode.length === 6 ? 'rgba(0, 123, 255, 0.9)' : 'rgba(0, 123, 255, 0.5)', 
								border: '1px solid rgba(0, 123, 255, 0.9)', 
								color: '#fff',
								padding: '0.75rem',
								transition: 'all 0.2s ease'
							}}
							disabled={loading || otpCode.length !== 6}
						>
							{loading ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Verifying...
								</>
							) : (
								<>
									<i className="fas fa-key me-2"></i>
									Verify Code
								</>
							)}
						</button>
						
						<button 
							type="button" 
							className="btn btn-outline-secondary d-block w-100" 
							disabled={loading} 
							onClick={resetToLogin}
							style={{color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.3)'}}
						>
							<i className="fas fa-arrow-left me-2"></i>
							Back to Login
						</button>
					</form>
				)}

				{stage === 'mfa_setup' && (
					<form onSubmit={handleVerifySetup} style={{width: '100%'}} noValidate>
						<h1 className="text-center" style={{color: '#fff', marginBottom: '0.5rem'}}>
							<i className="fas fa-shield-alt me-2"></i>
							Enable Multi‑Factor Authentication
						</h1>
						<div className="text-center mb-4" style={{color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem'}}>
							Secure your account with an authenticator app
						</div>
						
						{error && (
							<div className="alert alert-danger mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-exclamation-circle me-2"></i>
								{error}
							</div>
						)}
						{success && (
							<div className="alert alert-success mb-3 d-flex align-items-center" role="alert">
								<i className="fas fa-check-circle me-2"></i>
								{success}
							</div>
						)}
						
						<div className="text-center mb-4" style={{position: 'relative', zIndex: 10}}>
							{otpauthUrl && (
								<div style={{display: 'inline-block', padding: '16px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)'}}>
									<QRCode value={otpauthUrl} size={200} />
								</div>
							)}
							<div className="mt-3" style={{color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem'}}>
								<i className="fas fa-mobile-alt me-2"></i>
								Scan with Google Authenticator, Microsoft Authenticator, or similar app
							</div>
							{mfaSecret && (
								<div className="mt-3" style={{color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem'}}>
									<div className="mb-1">Or enter this key manually:</div>
									<code style={{
										background: 'rgba(255,255,255,0.1)', 
										color: '#fff', 
										padding: '0.25rem 0.5rem', 
										borderRadius: '4px',
										fontSize: '0.8rem',
										letterSpacing: '1px'
									}}>
										{mfaSecret}
									</code>
								</div>
							)}
						</div>
						
						<div className="mb-4">
							<label className="form-label" style={{color: '#fff', fontWeight: '500'}}>
								Verification Code
							</label>
							<input
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={6}
								value={otpCode}
								onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
								className={`form-control form-control-lg text-center ${otpCode.length === 6 ? 'is-valid' : ''}`}
								style={{
									backgroundColor: 'rgba(255, 255, 255, 0.9)', 
									border: '1px solid rgba(255, 255, 255, 0.3)', 
									color: '#333',
									fontSize: '1.5rem',
									letterSpacing: '0.5rem',
									fontFamily: 'monospace'
								}}
								placeholder="000000"
								required
								disabled={loading}
								autoComplete="one-time-code"
							/>
							<div className="form-text text-center mt-2" style={{color: 'rgba(255, 255, 255, 0.7)'}}>
								Enter the 6-digit code from your authenticator app
							</div>
						</div>
						
						<button 
							type="submit"
							className="btn btn-outline-theme d-block w-100 fw-500 mb-2"
							style={{
								backgroundColor: otpCode.length === 6 ? 'rgba(0, 123, 255, 0.9)' : 'rgba(0, 123, 255, 0.5)', 
								border: '1px solid rgba(0, 123, 255, 0.9)', 
								color: '#fff',
								padding: '0.75rem',
								transition: 'all 0.2s ease'
							}}
							disabled={loading || otpCode.length !== 6}
						>
							{loading ? (
								<>
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									Verifying...
								</>
							) : (
								<>
									<i className="fas fa-shield-alt me-2"></i>
									Verify and Enable MFA
								</>
							)}
						</button>
						
						<button 
							type="button" 
							className="btn btn-outline-secondary d-block w-100" 
							disabled={loading} 
							onClick={resetToLogin}
							style={{color: 'rgba(255, 255, 255, 0.8)', borderColor: 'rgba(255, 255, 255, 0.3)'}}
						>
							<i className="fas fa-arrow-left me-2"></i>
							Back to Login
						</button>
					</form>
				)}
			</div>
		</div>
	)
}

export default PagesLogin;